import { useState, useEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Users, AppWindow, Zap, TrendingUp, Loader2, Download, AlertTriangle, Activity } from 'lucide-react'
import {
  getAdminAnalytics,
  getAnalyticsTrends,
  type AdminAnalytics,
  type TrendPoint,
} from '../../../lib/api'
import { CHART_PALETTE } from '../../../lib/chart-colors'
import StatCard from '../../../components/analytics/StatCard'
import TrendChart from '../../../components/analytics/TrendChart'
import PeriodSelector from '../../../components/analytics/PeriodSelector'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import { arrayToCSV, downloadCSV } from '../../../lib/csv-export'
import AutoRefreshNotice from '../../../components/AutoRefreshNotice'

const NAV_ITEMS = [
  { label: 'Overview', path: '/admin/analytics' },
  { label: 'Users', path: '/admin/analytics/users' },
  { label: 'Apps', path: '/admin/analytics/apps' },
  { label: 'Activity', path: '/admin/analytics/activity' },
  { label: 'Features', path: '/admin/analytics/features' },
  { label: 'System', path: '/admin/analytics/system' },
  { label: 'Chatbot', path: '/admin/analytics/chatbot' },
  { label: 'Templates', path: '/admin/analytics/templates' },
]

function AnalyticsNav() {
  const location = useLocation()
  return (
    <nav className="border-b border-border mb-6">
      <div className="flex gap-6">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path
          return (
            <Link
              key={item.path}
              to={item.path}
              className={
                isActive
                  ? 'border-b-2 border-primary text-foreground font-medium pb-3 text-sm'
                  : 'text-muted-foreground hover:text-foreground pb-3 text-sm'
              }
            >
              {item.label}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

export { AnalyticsNav, NAV_ITEMS }

export default function AnalyticsOverview() {
  const [period, setPeriod] = useState<string>('30d')
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [trends, setTrends] = useState<TrendPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    try {
      const [analyticsData, trendsData] = await Promise.all([
        getAdminAnalytics(period),
        getAnalyticsTrends(period),
      ])
      setAnalytics(analyticsData)
      setTrends(trendsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
      throw err // Allow useAutoRefresh to detect the error for backoff
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    setLoading(true)
    loadData()
  }, [loadData])

  const refreshState = useAutoRefresh(loadData, {
    baseInterval: 60000,
    maxInterval: 300000,
  })

  function handleExportCSV() {
    if (trends.length === 0) return
    const csv = arrayToCSV(trends, [
      { key: 'date', label: 'Date' },
      { key: 'active_users', label: 'Active Users' },
      { key: 'new_users', label: 'New Users' },
      { key: 'total_apps', label: 'Total Apps' },
      { key: 'deploys', label: 'Deploys' },
    ])
    downloadCSV(csv, `analytics-overview-${period}.csv`)
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Analytics</h1>
          <p className="text-muted-foreground">Platform health and usage metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <button
            onClick={handleExportCSV}
            disabled={trends.length === 0}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
            title="Download data as CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
        </div>
      </div>

      {/* Sub-navigation */}
      <AnalyticsNav />

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      <AutoRefreshNotice
        errorMessage={refreshState.lastErrorMessage}
        currentIntervalMs={refreshState.currentInterval}
        retryAfterSeconds={refreshState.retryAfterSeconds}
      />

      {/* Loading state */}
      {loading && !analytics ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : !analytics ? (
        <div className="text-center py-12 text-muted-foreground">
          No analytics data available
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <StatCard
              label="Active Users (DAU)"
              value={analytics.platform.active_users_daily}
              icon={Users}
            />
            <StatCard
              label="Weekly Active"
              value={analytics.platform.active_users_weekly}
              icon={Users}
            />
            <StatCard
              label="Monthly Active"
              value={analytics.platform.active_users_monthly}
              icon={Users}
            />
            <StatCard
              label="Total Apps"
              value={analytics.platform.total_apps}
              icon={AppWindow}
              href="/admin/apps"
            />
            <StatCard
              label="Running Apps"
              value={analytics.platform.running_apps}
              icon={Zap}
            />
            <StatCard
              label="Apps in Error"
              value={analytics.platform.apps_in_error}
              icon={AlertTriangle}
            />
            <StatCard
              label="Deploy Attempts"
              value={analytics.platform.deploy_attempts}
              icon={Activity}
            />
            <StatCard
              label="Running Ratio"
              value={`${analytics.platform.running_app_ratio}%`}
              icon={TrendingUp}
            />
          </div>
          <p className="mb-8 text-sm text-muted-foreground">
            Running Ratio reflects the share of apps currently in a running state. Deploy Attempts
            counts `app_start` and `app_rebuild` actions for the selected period.
          </p>

          {/* Trend charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-semibold mb-4">Active Users</h3>
              <TrendChart
                data={trends}
                lines={[
                  { dataKey: 'active_users', label: 'Active Users', color: CHART_PALETTE[0] },
                ]}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-semibold mb-4">Deploys</h3>
              <TrendChart
                data={trends}
                lines={[
                  { dataKey: 'deploys', label: 'Deploys', color: CHART_PALETTE[1] },
                ]}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-semibold mb-4">New Users</h3>
              <TrendChart
                data={trends}
                lines={[
                  { dataKey: 'new_users', label: 'New Users', color: CHART_PALETTE[2] },
                ]}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
